<template>
  <div>
    <div class="tw-relative tw-inline-block">
      <button
        @click="showFilters = !showFilters"
        data-filter-toggle
        class="tw-flex tw-text-xs tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-white tw-bg-secondary-500 tw-border tw-border-solid tw-border-secondary-600 tw-rounded-md hover:tw-bg-secondary-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-secondary-500 focus:tw-border-secondary-500"
      >
        <div class="tw-text-white">
          <FilterIcon />
        </div>
        {{ t('common.forms.filters') }}
      </button>
      <Badge
        v-if="activeFilterCount > 0"
        variant="custom"
        class="tw-absolute -tw-top-2 -tw-right-2 tw-h-5 tw-w-5 tw-flex tw-items-center tw-justify-center tw-text-xs tw-bg-red-200 tw-text-red-600 tw-rounded-full"
      >
        {{ activeFilterCount }}
      </Badge>
    </div>

    <!-- Backdrop -->
    <Transition
      enter-active-class="tw-transition-opacity tw-duration-300"
      leave-active-class="tw-transition-opacity tw-duration-300"
      enter-from-class="tw-opacity-0"
      enter-to-class="tw-opacity-100"
      leave-from-class="tw-opacity-100"
      leave-to-class="tw-opacity-0"
    >
      <div
        v-if="showFilters"
        class="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-z-[150]"
        @click="showFilters = false"
      ></div>
    </Transition>

    <!-- Offcanvas Panel -->
    <Transition
      enter-active-class="tw-transition-transform tw-duration-300 tw-ease-in-out"
      leave-active-class="tw-transition-transform tw-duration-300 tw-ease-in-out"
      enter-from-class="tw-translate-x-full"
      enter-to-class="tw-translate-x-0"
      leave-from-class="tw-translate-x-0"
      leave-to-class="tw-translate-x-full"
    >
      <div
        v-if="showFilters"
        class="tw-fixed tw-top-0 tw-right-0 tw-h-full tw-w-96 tw-bg-white tw-shadow-xl tw-z-[200] tw-flex tw-flex-col"
      >
        <!-- Header -->
        <div
          class="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-b tw-border-gray-200 tw-bg-gray-50"
        >
          <div class="tw-flex tw-items-center tw-gap-2 tw-justify-center">
            <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-0">
              {{ t('common.forms.filters') }}
            </h2>
            <span class="tw-font-normal tw-text-xs tw-text-gray-500">
              ({{ activeFilterCount }} / {{ filterableColumns.length }})
            </span>
          </div>
          <button
            @click="showFilters = false"
            class="tw-p-2 tw-text-gray-400 hover:tw-text-gray-600 tw-rounded-md hover:tw-bg-gray-100 tw-transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              color="currentColor"
              fill="none"
            >
              <path
                d="M19.0005 4.99988L5.00049 18.9999M5.00049 4.99988L19.0005 18.9999"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>

        <!-- Fixed Header with Search -->
        <div class="tw-p-4 tw-border-b tw-border-gray-200 tw-bg-white tw-sticky tw-top-0 tw-z-10">
          <h3 class="tw-text-xs tw-text-gray-500 tw-mb-3">
            {{ t('common.forms.column_filters') }}
          </h3>

          <!-- Search Bar -->
          <div class="tw-mb-4 tw-relative">
            <!-- Search Icon -->
            <div
              class="tw-absolute tw-left-3 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-text-gray-400 tw-pointer-events-none"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M17.5 17.5L22 22M20 11C20 15.9706 15.9706 20 11 20C6.02944 20 2 15.9706 2 11C2 6.02944 6.02944 2 11 2C15.9706 2 20 6.02944 20 11Z"
                />
              </svg>
            </div>

            <input
              ref="searchInput"
              v-model="filterSearch"
              :placeholder="t('common.forms.find_column')"
              class="tw-w-full tw-font-normal tw-pl-10 tw-pr-10 tw-py-2 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-secondary-300 tw-focus:border-secondary-300 tw-transition-colors"
            />

            <!-- Clear Button -->
            <button
              v-if="filterSearch.trim()"
              @click.stop="filterSearch = ''"
              class="tw-absolute tw-right-3 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M19 5L5 19M5 5L19 19"
                />
              </svg>
            </button>
          </div>

          <!-- Results Count -->
          <div v-if="filterSearch.trim()" class="tw-text-xs tw-text-gray-500">
            Showing {{ filteredColumns.length }} of {{ filterableColumns.length }} columns
          </div>
        </div>

        <!-- Scrollable Content -->
        <div class="tw-flex-1 tw-overflow-y-auto tw-p-4 tw-pb-20">
          <div class="tw-space-y-2">
            <div v-for="column in filteredColumns" :key="column.id">
              <label class="tw-text-sm tw-font-medium tw-text-gray-600">
                {{ column.columnDef.header }}
              </label>

              <!-- Custom filter slot -->
              <slot
                :name="`filter-${column.id}`"
                :column="column"
                :set-filter="(value: any) => handleFilterUpdate(column, value)"
                :filter-value="column.getFilterValue()"
              >
                <!-- Status/Boolean column filter (dropdown) -->
                <FancyBooleanFilter
                  v-if="(column.columnDef as FancyTableColumnDef<any>).filterType === FilterType.BOOLEAN"
                  :header="{ column } as any"
                  @update:filter="value => handleFilterUpdate(column, value)"
                />

                <!-- Date range filter -->
                <FancyServerDateRangeFilter
                  v-else-if="
                  column.getCanFilter() &&
                  (column.columnDef as FancyTableColumnDef<any>).fieldType === FieldType.DATE
                "
                  :header="{ column } as any"
                  :value="getPendingFilterValue(column) as any"
                  @update:filter="value => handleFilterUpdate(column, value)"
                />

                <!-- number range filter -->
                <FancyNumberRangeFilter
                  v-else-if="(column.columnDef as FancyTableColumnDef<any>).filterType === FilterType.NUMBER_RANGE"
                  :header="{ column } as any"
                  :use-currency="(column.columnDef as FancyTableColumnDef<any>).fieldType === FieldType.MONEY"
                  @update:filter="value => handleFilterUpdate(column, value, true)"
                />

                <!-- multi select filter -->
                <FancyMultiSelectFilter
                  v-else-if="(column.columnDef as FancyTableColumnDef<any>).filterType === FilterType.MULTI_SELECT"
                  :header="{ column } as any"
                  @update:filter="value => handleFilterUpdate(column, value)"
                />

                <!-- Regular text input filters -->
                <input
                  v-else
                  :value="getPendingFilterValue(column) as string"
                  @input="handleTextFilterUpdate(column, ($event.target as HTMLInputElement).value)"
                  :placeholder="
                    t('common.titles.filter', {
                      columnName: column.columnDef.header,
                    })
                  "
                  class="tw-w-full tw-font-normal tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-secondary-300 tw-focus:border-secondary-300 tw-transition-colors"
                />
              </slot>
            </div>
          </div>
        </div>

        <!-- Footer with actions -->
        <div
          class="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-p-4 tw-border-t tw-border-gray-200 tw-bg-gray-50 tw-space-y-2"
        >
          <!-- Apply button only shown in manual mode -->
          <button
            v-if="!autoFilter"
            @click.stop="applyFilters"
            class="tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-secondary-600 tw-rounded-md hover:tw-bg-secondary-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-secondary-500 tw-transition-colors"
          >
            {{ t('common.forms.apply') }}
          </button>
          <button
            @click.stop="clearAllFilters"
            class="tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-red-600 tw-border tw-border-red-200 tw-rounded-md hover:tw-bg-red-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-red-500 tw-transition-colors"
            :disabled="activeFilterCount === 0"
            :class="{
              'tw-opacity-50 tw-cursor-not-allowed': activeFilterCount === 0,
            }"
          >
            {{ t('common.forms.clear_all_filters') }}
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts" generic="T">
import { useI18n } from 'vue-i18n';
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import type { Table, Column } from '@tanstack/vue-table';
import type { FancyTableColumnDef } from './types';
import { FieldType, FilterType } from './types';
import FancyBooleanFilter from './Rendering/components/FancyBooleanFilter.vue';
import FancyServerDateRangeFilter from './Rendering/components/FancyServerDateRangeFilter.vue';
import FancyNumberRangeFilter from './Rendering/components/FancyNumberRangeFilter.vue';
import FancyMultiSelectFilter from './Rendering/components/FancyMultiSelectFilter.vue';
import FilterIcon from '@/assets/icons/Filter.svg';
import { Badge } from '@/components/ui/badge';

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    table: Table<T>;
    /**
     * Enable auto-filtering with debounce instead of requiring Apply button
     * @default false
     */
    autoFilter?: boolean;
    /**
     * Debounce delay in milliseconds for auto-filtering
     * @default 500
     */
    debounceDelay?: number;
  }>(),
  {
    autoFilter: false,
    debounceDelay: 500,
  }
);

const emit = defineEmits<{
  'filter-change': [];
}>();

const showFilters = ref(false);
const filterSearch = ref('');
const searchInput = ref<HTMLInputElement | null>(null);

// Temporary filter values that are not applied yet (only used when autoFilter is false)
const pendingFilters = ref<Map<string, any>>(new Map());

// Debounce timer for auto-filtering
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

// Get all filterable columns
const filterableColumns = computed(() => {
  const columnOrder = props.table.getState().columnOrder;
  const allColumns = props.table.getAllColumns();

  const columnMap = new Map(allColumns.map(col => [col.id, col]));

  const visibleColumnsInOrder: any[] = [];

  if (columnOrder.length > 0) {
    columnOrder.forEach(columnId => {
      const column = columnMap.get(columnId);
      if (column && column.getCanFilter() && column.getIsVisible()) {
        visibleColumnsInOrder.push(column);
      }
    });
  }

  allColumns.forEach(column => {
    if (column.getCanFilter() && column.getIsVisible() && !visibleColumnsInOrder.includes(column)) {
      visibleColumnsInOrder.push(column);
    }
  });

  return visibleColumnsInOrder;
});

// Filter columns based on search term
const filteredColumns = computed(() => {
  if (!filterSearch.value.trim()) {
    return filterableColumns.value;
  }

  return filterableColumns.value.filter(column => {
    const header = column.columnDef.header?.toString().toLowerCase() || '';
    return header.includes(filterSearch.value.toLowerCase());
  });
});

// Get active filters
const activeFilters = computed(() => {
  return filterableColumns.value
    .filter(column => {
      // In auto-filter mode, only check actual filter values
      if (props.autoFilter) {
        const filterValue = column.getFilterValue();
        return filterValue !== undefined && filterValue !== null && filterValue !== '';
      }

      // In manual mode, check pending filters first
      if (pendingFilters.value.has(column.id)) {
        const pendingValue = pendingFilters.value.get(column.id);
        return pendingValue !== undefined && pendingValue !== null && pendingValue !== '';
      }
      // Otherwise check the actual filter value
      const filterValue = column.getFilterValue();
      return filterValue !== undefined && filterValue !== null && filterValue !== '';
    })
    .map(column => ({
      id: column.id,
      columnHeader: column.columnDef.header,
      value: props.autoFilter
        ? column.getFilterValue()
        : pendingFilters.value.has(column.id)
        ? pendingFilters.value.get(column.id)
        : column.getFilterValue(),
    }));
});

// Count active filters
const activeFilterCount = computed(() => {
  return activeFilters.value.length;
});

// Get pending filter value for a column
const getPendingFilterValue = (column: Column<T, unknown>) => {
  // In auto-filter mode, always use actual column filter value
  if (props.autoFilter) {
    return column.getFilterValue() || '';
  }

  // In manual mode, use pending filters if available
  if (pendingFilters.value.has(column.id)) {
    return pendingFilters.value.get(column.id) || '';
  }
  return column.getFilterValue() || '';
};

// Debounced filter application (for text inputs only)
const debouncedApplyFilter = (column: Column<T, unknown>, value: any) => {
  // Clear existing timer
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  // Set new timer
  debounceTimer = setTimeout(() => {
    column.setFilterValue(value);
    emit('filter-change');
  }, props.debounceDelay);
};

// Handle filter updates (non-text filters like Boolean, MultiSelect, etc.)
const handleFilterUpdate = (column: Column<T, unknown>, value: any, shouldDebounce = false) => {
  const normalizedValue = value === undefined || value === null || value === '' ? undefined : value;

  if (props.autoFilter) {
    if (shouldDebounce) {
      // Text inputs: Apply with debouncing
      debouncedApplyFilter(column, normalizedValue);
    } else {
      // Non-text filters: Apply immediately without debounce
      column.setFilterValue(normalizedValue);
      emit('filter-change');
    }
  } else {
    // Manual mode: Store in pending filters
    if (normalizedValue === undefined) {
      pendingFilters.value.delete(column.id);
    } else {
      pendingFilters.value.set(column.id, normalizedValue);
    }
  }
};

// Handle text filter updates (with debouncing)
const handleTextFilterUpdate = (column: Column<T, unknown>, value: string) => {
  handleFilterUpdate(column, value || undefined, true);
};

// Apply all pending filters
const applyFilters = () => {
  // Apply all pending filters to the table
  pendingFilters.value.forEach((value, columnId) => {
    const column = props.table.getColumn(columnId);
    if (column) {
      column.setFilterValue(value);
    }
  });

  // Clear any filters that were removed
  filterableColumns.value.forEach(column => {
    if (!pendingFilters.value.has(column.id)) {
      const currentValue = column.getFilterValue();
      if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
        column.setFilterValue(undefined);
      }
    }
  });

  // Emit filter-change event to trigger server-side fetch
  emit('filter-change');

  // Close the filters panel
  showFilters.value = false;
};

// Clear all filters
const clearAllFilters = (event?: Event) => {
  if (event) {
    event.stopPropagation();
  }

  // Clear any pending debounce
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }

  // Clear pending filters (manual mode)
  pendingFilters.value.clear();

  // Clear actual filters
  filterableColumns.value.forEach(column => {
    column.setFilterValue(undefined);
  });

  // Emit filter-change event to trigger server-side fetch
  emit('filter-change');
};

// Watch for filter panel opening
const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement;
  const offcanvas = document.querySelector('.tw-fixed.tw-top-0.tw-right-0.tw-h-full');
  const button = document.querySelector('button[data-filter-toggle]');

  if (offcanvas && !offcanvas.contains(target) && !button?.contains(target)) {
    showFilters.value = false;
  }
};

// Initialize pending filters when opening the panel
const initializePendingFilters = () => {
  pendingFilters.value.clear();
  filterableColumns.value.forEach(column => {
    const filterValue = column.getFilterValue();
    if (filterValue !== undefined && filterValue !== null && filterValue !== '') {
      pendingFilters.value.set(column.id, filterValue);
    }
  });
};

// Watch for showFilters changes to initialize pending filters in manual mode
watch(showFilters, (newValue, oldValue) => {
  if (newValue && !oldValue && !props.autoFilter) {
    initializePendingFilters();
  }
});

// Handle keyboard shortcuts
const handleKeyDown = (event: KeyboardEvent) => {
  if ((event.ctrlKey || event.metaKey) && event.key === 'f' && showFilters.value) {
    event.preventDefault();
    searchInput.value?.focus();
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('keydown', handleKeyDown);

  // Clean up any pending debounce timer
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
});
</script>

<style scoped>
/* Transitions for smooth offcanvas animation */
.tw-transition-transform {
  transition-property: transform;
}

.tw-transition-opacity {
  transition-property: opacity;
}

.tw-duration-300 {
  transition-duration: 300ms;
}

.tw-ease-in-out {
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
